import { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';

import verificationIcon from '../../assets/svgs/imId-verification.svg';
import imIdInfo from '../../assets/svgs/imId-info.svg';
import ActionLink from '../../components/actionLink/ActionLink';
import RegistrationPage, {
    RegistrationSubmitFooter,
} from '../../components/layouts/RegistrationPage';
import { useSocket } from '../../hooks/useSocket';
import { useSocketEvent } from '../../hooks/useSocketEvent';
import api from '../../utils/api';
import { getAccountId, setAccount } from '../../utils/storage';
import type { AccountInfo } from '../../utils/storage';
import { getImIdErrorCodeFromApiError } from '../../utils/imIdApiError';
import useLogger from '../../hooks/useLogger';
import { IMID_VERIFICATION_ACTIONS, LOG_STATUSES } from '../../logging/LoggingConstants';
import {
    AnalyticsEvent,
    durationSecSince,
    getBiometricStartedAt,
    markBiometricStarted,
    setAnalyticsVerificationMethod,
    trackBiometricVerificationFailed,
    useAnalytics,
} from '../../analytics';
import PageHeader from '../../components/PageHeader';
import CustomButton from '../../components/customButton/CustomButton';

export enum ImIdVerificationStatuses {
    'SUCCESS' = '200',
    'INCORRECT_SSN' = '2000',
    'PHONE_NOT_CONNECTED' = '2001',
    'REQUEST_CANCELLED' = '2002',
    'CONNECTION_LOST' = '2003',
    'GENERAL_ERROR' = '9999',
}

type AuthaasTokenResponse = {
    success?: boolean;
    correlation_id?: string | null;
};

type VerificationStatusResponse = {
    status: 'pending' | 'success' | 'failed';
    errorCode?: string | null;
    correlationId?: string;
    account?: AccountInfo | null;
};

const POLL_MS = 2500;

const ImIdVerificationPending = () => {
    const { t } = useTranslation();
    const { log } = useLogger();
    const { track } = useAnalytics();
    const [secondsLeft, setSecondsLeft] = useState(120);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const accountId = getAccountId() || "";

    const phoneNumber = searchParams.get('phoneNumber') || '';
    const resultUrl = `/imid-verification-result?phoneNumber=${encodeURIComponent(phoneNumber)}`;

    const isInitializedRef = useRef(false);
    const navigatedRef = useRef(false);
    const biometricStartedRef = useRef(false);
    const [correlationId, setCorrelationId] = useState<string | null>(null);

    const { connect, disconnect, isConnected } = useSocket();

    useEffect(() => {
        if (biometricStartedRef.current) {
            return;
        }
        biometricStartedRef.current = true;
        setAnalyticsVerificationMethod('imID');
        markBiometricStarted();
        track(AnalyticsEvent.BIOMETRIC_VERIFICATION_STARTED, {
            verification_method: 'imID',
        });
    }, [track]);

    const completeSuccessfulVerification = useCallback((verifiedAccount?: AccountInfo | null) => {
        if (navigatedRef.current) return;
        navigatedRef.current = true;

        if (verifiedAccount) {
            setAccount(verifiedAccount);
        }

        log(IMID_VERIFICATION_ACTIONS.IMID_VERIFICATION_COMPLETE, LOG_STATUSES.SUCCESS);
        track(AnalyticsEvent.BIOMETRIC_VERIFICATION_COMPLETED, {
            verification_method: 'imID',
            duration_sec: durationSecSince(getBiometricStartedAt()) ?? 0,
        });

        navigate(`/imid-verification-result?resultCode=${ImIdVerificationStatuses.SUCCESS}`, {
            state: { account: verifiedAccount ?? undefined },
        });
    }, [log, navigate, track]);

    const navigateToError = useCallback(
        (resultCode: ImIdVerificationStatuses | string) => {
            if (navigatedRef.current) return;
            navigatedRef.current = true;
            log(IMID_VERIFICATION_ACTIONS.IMID_VERIFICATION_FAILURE, LOG_STATUSES.FAILURE);
            // Recoverable — retry / switch to Sumsub; do not fire registration_failed
            trackBiometricVerificationFailed({
                verification_method: 'imID',
                errorCode: String(resultCode),
                context: 'imid',
            });
            navigate(`${resultUrl}&resultCode=${resultCode}`);
        },
        [log, navigate, resultUrl, track]
    );

    const requestAuthToken = useCallback(async (): Promise<string | null> => {
        if (!accountId || !phoneNumber) {
            return null;
        }

        try {
            const res = (await api.get('/imid/request-authaas-token', {
                params: {
                    accountId,
                    phoneNumber,
                },
            })) as AuthaasTokenResponse;

            if (res?.success === false) {
                throw Object.assign(new Error('imID token request failed'), {
                    response: { data: { message: ImIdVerificationStatuses.PHONE_NOT_CONNECTED } },
                });
            }

            log(IMID_VERIFICATION_ACTIONS.REQUEST_IMID_TOKEN, LOG_STATUSES.SUCCESS);
            return res?.correlation_id ?? null;
        } catch (error) {
            log(IMID_VERIFICATION_ACTIONS.REQUEST_IMID_TOKEN, LOG_STATUSES.FAILURE);
            throw error;
        }
    }, [accountId, phoneNumber, log]);

    useEffect(() => {
        if (!phoneNumber) {
            navigate('/imid-verification');
            return;
        }

        if (isInitializedRef.current) {
            return;
        }

        const initializeVerification = async () => {
            isInitializedRef.current = true;
            navigatedRef.current = false;

            try {
                await connect('/imid', { accountId, phoneNumber });
                log(IMID_VERIFICATION_ACTIONS.IMID_VERIFICATION_START, LOG_STATUSES.SUCCESS);

                const cid = await requestAuthToken();
                if (cid) {
                    setCorrelationId(cid);
                } else {
                    navigateToError(ImIdVerificationStatuses.GENERAL_ERROR);
                }
            } catch (error) {
                isInitializedRef.current = false;
                log(IMID_VERIFICATION_ACTIONS.IMID_VERIFICATION_FAILURE, LOG_STATUSES.FAILURE);
                navigateToError(
                    getImIdErrorCodeFromApiError(
                        error,
                        ImIdVerificationStatuses.CONNECTION_LOST
                    )
                );
            }
        };

        void initializeVerification();

        return () => {
            isInitializedRef.current = false;
            disconnect();
        };
    }, [accountId, phoneNumber, connect, disconnect, navigate, requestAuthToken, log, navigateToError]);

    /** Postgres-backed status so completion works when callback hits another pod than the WebSocket. */
    useEffect(() => {
        if (!correlationId || !accountId) {
            return;
        }

        let cancelled = false;

        const poll = async () => {
            if (cancelled || navigatedRef.current) return;
            try {
                const status = (await api.get('/imid/verification-status', {
                    params: { accountId, correlationId },
                })) as VerificationStatusResponse;

                if (cancelled || navigatedRef.current) return;

                if (status.status === 'success') {
                    completeSuccessfulVerification(status.account);
                    return;
                }
                if (status.status === 'failed') {
                    const code =
                        (status.errorCode as ImIdVerificationStatuses) ||
                        ImIdVerificationStatuses.GENERAL_ERROR;
                    navigateToError(code);
                }
            } catch {
                // Transient errors (network); next interval retries
            }
        };

        const id = window.setInterval(() => {
            void poll();
        }, POLL_MS);
        void poll();

        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [correlationId, accountId, completeSuccessfulVerification, navigateToError]);

    const checkVerificationStatus = useCallback(async () => {
        if (navigatedRef.current || !accountId) {
            return;
        }

        try {
            const status = (await api.get('/imid/verification-status', {
                params: correlationId ? { accountId, correlationId } : { accountId },
            })) as VerificationStatusResponse;

            if (navigatedRef.current) return;

            if (status.status === 'success') {
                completeSuccessfulVerification(status.account);
            } else if (status.status === 'failed') {
                navigateToError(
                    (status.errorCode as ImIdVerificationStatuses) ||
                        ImIdVerificationStatuses.GENERAL_ERROR
                );
            }
        } catch {
            // Transient errors (network); polling retries
        }
    }, [accountId, correlationId, completeSuccessfulVerification, navigateToError]);

    const onImidChannel = useCallback(
        (data: { correlationId?: string; userInfo?: { correlation_id?: string } }) => {
            if (navigatedRef.current) return;
            if (correlationId) {
                const incoming = data?.correlationId ?? data?.userInfo?.correlation_id;
                if (incoming && incoming !== correlationId) {
                    return;
                }
            }
            void checkVerificationStatus();
        },
        [correlationId, checkVerificationStatus]
    );

    useSocketEvent('imid_chanel', onImidChannel, isConnected);

    useSocketEvent(
        'registered',
        () => {
            log(IMID_VERIFICATION_ACTIONS.IMID_VERIFICATION_REGISTERED, LOG_STATUSES.SUCCESS);
        },
        isConnected
    );

    useSocketEvent(
        'error',
        () => {
            navigateToError(ImIdVerificationStatuses.GENERAL_ERROR);
        },
        isConnected
    );

    useSocketEvent(
        'imid_error',
        (data: { success: boolean; message: string; errorCode: ImIdVerificationStatuses }) => {
            const errorCode = data?.errorCode || ImIdVerificationStatuses.GENERAL_ERROR;
            navigateToError(errorCode);
        },
        isConnected
    );

    useEffect(() => {
        if (!secondsLeft) return;
        const timer = setInterval(() => {
            setSecondsLeft((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [secondsLeft]);

    const handleResend = async () => {
        if (!secondsLeft && accountId && phoneNumber) {
            setSecondsLeft(120);
            navigatedRef.current = false;
            try {
                if (!isConnected) {
                    await connect('/imid', { accountId, phoneNumber });
                    log(IMID_VERIFICATION_ACTIONS.IMID_VERIFICATION_START, LOG_STATUSES.SUCCESS);
                }
                const cid = await requestAuthToken();
                if (cid) {
                    setCorrelationId(cid);
                } else {
                    navigateToError(ImIdVerificationStatuses.GENERAL_ERROR);
                }
            } catch (error) {
                log(IMID_VERIFICATION_ACTIONS.IMID_VERIFICATION_FAILURE, LOG_STATUSES.FAILURE);
                navigateToError(
                    getImIdErrorCodeFromApiError(
                        error,
                        ImIdVerificationStatuses.CONNECTION_LOST
                    )
                );
            }
        }
    };

    const handleBack = () => navigate('/imid-verification');

    return (
        <RegistrationPage
            contentClassName="max-w-full md:max-w-[32.5rem] mx-auto"
            stickyFooter
            headerProps={{ showBack: true, onBack: handleBack }}
            footer={
                <div className="flex flex-col items-center gap-6 pt-4 md:pt-0 mt-0 md:mt-4 w-full bg-white">
                    <CustomButton
                        type="button"
                        variant="text"
                        onClick={handleResend}
                        disabled={!!secondsLeft}
                    >
                        {t('imIdVerification.resend')}
                    </CustomButton>
                    <ActionLink
                        to="/imid-verification"
                        disabled={!!secondsLeft}
                        className="w-full px-3 md:max-w-full md:px-6 lg:max-w-80"
                    >
                        {t('imIdVerification.changePhoneNumber')}
                    </ActionLink>
                </div>
            }
        >
            <PageHeader
                title={t('imIdVerification.followInstructionsOnPhone')}
                lead={t('imIdVerification.identificationRequestSent', {
                    phoneNumber: phoneNumber || '',
                })}
                imgSrc={verificationIcon}
            />
            <div className="flex-1 flex flex-col items-center gap-6 mt-10">
                {!!secondsLeft && (
                    <p className="text-base leading-relaxed font-medium text-custom-blue-primary mb-6">
                        {t('imIdVerification.seconds', { count: secondsLeft })}
                    </p>
                )}
            </div>
        </RegistrationPage>
    );
};

export default ImIdVerificationPending;
